<template>
  <div class="multiple-choice-option-view" :class="{ correctOption: props.isCorrectOption }">
    <div class="option-set">
      <div class="option-wrapper">
        <span class="icon">
          <svg-icon v-if="props.isCorrectOption" name="Checkmark16" kind="indicative" />
        </span>
        <rich-text-wrapper
          class="option-answer"
          :content="getOptionAnswer(props.option.answer)"
          :expandable="true"
          :expanded="true"
          :can-annotate="canAccessAnnotations"
          :anchor="optionBodyAnchor"
          :resource-type="'LESSON_COMPONENT'"
          :annotation-resource-id="questionAnnotationResourceId"
        />
      </div>
      <!-- Marks -->
      <dl v-if="props.showOptionMark" class="field-set marks">
        <dd class="field-value">{{ optionMark }}</dd>
      </dl>
    </div>
    <rich-text-card
      v-if="props.option.feedback"
      class="option-feedback"
      :title="t('question_option_title_feedback')"
      :content="props.option.feedback"
      truncation-type="secondary"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import SecurityUtils from "@utils/security.utils";
import RichTextWrapper from "@plugins/richtext/components/RichTextWrapper.vue";
import { buildMcqOptionBodyAnchor } from "@modules/annotation/service/annotation.anchor.utils.ts";
import { useAnnotationPermissions } from "@modules/annotation/composables/use-annotation-permissions.ts";
import { useQuestionAnnotation } from "@modules/editor/composables/use-question-annotation.ts";

interface Option {
  answer: string;
  mark?: string;
  feedback?: string;
}

interface Props {
  option?: Option;
  isMultipleChoice?: boolean;
  isCorrectOption?: boolean;
  isSliderDisplayMode?: boolean;
  showOptionMark?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  option: () => ({}),
  isMultipleChoice: false,
  isCorrectOption: false,
  isSliderDisplayMode: false,
  showOptionMark: false,
});

const { t } = useI18n();
const { canAccessAnnotations } = useAnnotationPermissions();

const { questionAnchor, questionAnnotationResourceId } = useQuestionAnnotation();

// Computed

const optionBodyAnchor = computed(() => {
  return questionAnchor.value
    ? buildMcqOptionBodyAnchor(questionAnchor.value, props.option.id)
    : "";
});

const optionMark = computed(() => {
  return `[${props.option.mark}]`;
});

// Methods
const getOptionAnswer = (answer: string) => {
  if (props.isSliderDisplayMode) {
    return SecurityUtils.sanitizeText(answer);
  }
  return answer;
};
</script>

<style lang="scss">
@use "@styles/abstracts/functions" as *;
@use "@styles/abstracts/variables" as *;
@use "@styles/layout/breakpoint" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;

.multiple-choice-option-view {
  display: flex;
  flex-flow: row wrap;
  margin-bottom: $spacing-04;
  max-width: 100%;

  .option-set {
    flex: 1;
    padding: $spacing-04 $spacing-05;
    background-color: $ui-01;
    border: 1px solid $ui-03;
    border-radius: 10px 10px 0 0;
    max-width: 100%;

    &:only-child {
      border-radius: 10px;
    }

    .option-wrapper {
      display: flex;

      > .icon {
        display: flex;
        justify-content: center;
        align-items: center;
        flex: 0 0 1.25rem;
        width: 1.25rem;
        height: 1.25rem;
        background-color: $ui-background;
        border: 1px solid $ui-04;
        border-radius: 5px;
      }

      > .option-answer {
        margin-left: $spacing-05;
        flex: 1;
        max-width: calc(100% - 3rem);

        .rich-text-actions {
          margin-top: 0;
        }
      }
    }

    .field-set {
      &.marks {
        margin-top: $spacing-03;
      }
    }

    & + .option-feedback {
      // to overlap the border of option-set and option-feedback as 1px border
      margin-top: -1px;
    }
  }

  &.correctOption {
    .option-set {
      background-color: tint($state-positive, 90%);
      border: 1px solid transparent;
      color: $text-01;

      .option-wrapper {
        > .icon {
          border: 1px solid $state-positive;
          background-color: $icon-03;

          .sls-icon {
            [fill] {
              fill: $state-positive;
            }
          }
        }
      }
      .rich-text-content.collapsed {
        &:before {
          background-image: $correct-option-truncation;
        }
      }
    }
  }
  .option-feedback {
    flex: 0 0 100%;
    border: 1px solid $ui-03;
    border-radius: 0 0 10px 10px;
    max-width: 100%;
  }
}
</style>
